import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  Inject,
  ChangeDetectorRef,
} from '@angular/core';
import { Offer } from 'ng2-app/services/offers/offer.model';
import { Select, Store } from '@ngxs/store';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DealState } from 'ng2-app/store/deal/deal/deal.state';
import { Observable } from 'rxjs';
import { Deal } from 'ng2-app/models/dto/deal.model';
import { take } from 'rxjs/operators';
import {DealUtility} from 'ng2-app/util/deal/deal.utility';

@Component({
  selector: 'app-publish-offer-dialog',
  templateUrl: './publish-offer-dialog.component.html',
  styleUrls: ['./publish-offer-dialog.component.scss'],
  standalone: false
})
export class PublishOfferDialogComponent implements OnInit {
  @Select(DealState.deals) deals$: Observable<Deal[]>;

  @Input() offer: Offer;

  @Output() publishConfirmed = new EventEmitter<object>();

  deal: Deal | null = null;
  offerLoanType: string;
  paymentFrequencyShort: string;
  isPPPOffer: boolean;
  termDurationFormatted: string;
  rate: object | null;
  payback: string | number | null;
  costOfCapital: number | null;
  minPurchaseFee: number | null;
  paymentTerm: string | null;
  pppRound: number | null;
  originationFee: string | number | null;
  amountLabel: string;

  constructor(
    private store: Store,
    @Inject(MAT_DIALOG_DATA) public data,
    private dialogRef: MatDialogRef<PublishOfferDialogComponent>,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.deals$.pipe(take(1)).subscribe((deals) => {
      this.deal =
        deals.find((deal) => deal.id === this.data?.offer?.dealId) || null;

      this.offerLoanType = this.getLoanType();
      this.cdRef.detectChanges();
    });

    this.amountLabel = this.data?.offer?.loanProductType === 'loc' ? 'Initial draw amount' : 'Financing amount';
    const frequencyMap = {
      daily: 'day',
      semiweekly: 'sm.',
      weekly: 'wk.',
      biweekly: 'bw.',
      semimonthly: 'sm.',
      monthly: 'mo.',
      bimonthly: 'bm.',
    };
    this.paymentFrequencyShort = frequencyMap[this.data?.offer?.paymentFrequency] || '';

    const PPP_LOAN_TYPES = ['sbaPpp', 'sbaPPPSecond'];

    this.isPPPOffer = PPP_LOAN_TYPES.includes(
      this.data?.offer?.loanProductType
    );
    this.originationFee = this.data?.offer?.originationFee;
    this.isPPPOffer = PPP_LOAN_TYPES.includes(this.data?.offer?.loanProductType);

    this.termDurationFormatted = this.data?.offer?.term > 1 ? 'Months' : 'Month';
    this.rate = this.getRate();
    this.payback = this.getPaybackAmount();
    this.costOfCapital = this.getCostOfCapital(this?.data?.offer?.amount, this.payback);
    this.minPurchaseFee = this.getMinPurchaseFee();
    this.paymentTerm = this.getPaymentTerm();

    this.termDurationFormatted =
      this.data?.offer?.term > 1 ? 'Months' : 'Month';
    this.rate = this.getRate();
    this.payback = this.getPaybackAmount();
    this.costOfCapital = this.getCostOfCapital(
      this?.data?.offer?.amount,
      this.payback
    );
    this.minPurchaseFee = this.getMinPurchaseFee();
    this.paymentTerm = this.getPaymentTerm();

    if (this.isPPPOffer) {
      this.pppRound = PPP_LOAN_TYPES.indexOf(this.data?.offer?.loanProductType) + 1;
    }
  }

  closeModal() {
    this.dialogRef.close();
  }

  publish() {
    this.publishConfirmed.emit();
    this.closeModal();
  }

  private getRate() {
    const factor = this.data?.offer?.factor
      ? Number(this.data.offer.factor)
      : null;
    const interestRate = this.data?.offer?.interestRate ? Number(this.data.offer.interestRate) : null;

    if (factor != null && factor > 0) {
      return { type: 'Factor rate', value: factor.toString() };
    }
    if (interestRate != null && interestRate > 0) {
      return { type: 'Interest rate', value: `${interestRate}%` };
    }

    return null;
  }

  private getPaybackAmount() {
    if (this.data?.offer?.payback) {
      const payback = parseFloat(this.data.offer.payback);
      return Number.isFinite(payback) ? payback : null;
    }

    if (!this.data?.offer?.factor || !this.data?.offer?.amount) {
      return null;
    }
    const factorRate = Number(this.data.offer.factor);
    // calculate payback only for valid factor rate
    if (factorRate < 1 && factorRate >= 2) {
      return null;
    }
    const amount = parseFloat(this.data.offer.amount);
    if (!Number.isFinite(amount)) {
      return null;
    }

    return amount * factorRate;
  }

  private getCostOfCapital(loanAmount, paybackAmount) {
    if (!loanAmount || !paybackAmount) {
      return null;
    }
    const amount = parseFloat(loanAmount);
    const payback = parseFloat(paybackAmount);
    if (
      !Number.isFinite(amount) ||
      !Number.isFinite(payback) ||
      payback < amount
    ) {
      return null;
    }

    return payback - amount;
  }

  private getMinPurchaseFee() {
    const minPurchaseFee = this.data?.offer?.minPurchaseFee
      ? Number(this.data.offer.minPurchaseFee)
      : null;

    return minPurchaseFee != null && minPurchaseFee > 0 ? minPurchaseFee : null;
  }

  private getPaymentTerm() {
    const paymentTerm = this?.data?.offer.paymentTerm || '';
    if (!paymentTerm || !paymentTerm.trim()) {
      return null;
    }

    return paymentTerm.charAt(0).toUpperCase() + paymentTerm.slice(1);
  }

  private getLoanType() {
    const typeMap = {
      ach: 'Advance',
      loc: 'Line of credit',
      term: 'Term loan',
      sbaPpp: 'Paycheck protection program',
      sbaPPPSecond: 'Paycheck protection program',
    };
    return DealUtility.getOverriddenLoanType(this.deal) ??
      typeMap[this.data?.offer?.loanProductType ?? ''] ??
      this.deal?.loan_product?.category?.shortName ??
      '';
  }
}
